/**
 * Converts a CRON expression to human-readable text
 * 
 * CRON format: minute hour day-of-month month day-of-week
 * Example: "0 0-23/1 * * *" = every hour at minute 0
 */
export function formatCronExpression(cron: string): string {
  if (!cron || cron.trim() === '') {
    return 'Not scheduled'
  }

  // Handle NestJS CronExpression constants that are already human-readable
  if (cron.includes('every') || cron.includes('Every') || !cron.match(/^[\d*/\-, ]+$/)) {
    // It's already a description, not a CRON expression
    return formatDescription(cron)
  }

  const parts = cron.trim().split(/\s+/)
  
  if (parts.length < 5) {
    return cron // Return as-is if not a valid CRON format
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts

  // Common patterns
  if (cron === '0 0 * * *') {
    return 'Daily at midnight'
  }

  if (cron === '0 0 * * 0') {
    return 'Weekly on Sunday at midnight'
  }

  if (cron === '0 0 1 * *') {
    return 'Monthly on the 1st at midnight'
  }

  // Every hour pattern: "0 0-23/1 * * *" or "0 */1 * * *" or "0 0 * * *" (every hour at minute 0)
  if (minute === '0' && (hour === '0-23/1' || hour === '*/1' || hour === '*')) {
    return 'Every hour'
  }

  // Every N hours pattern: "0 */N * * *"
  const everyNHours = hour.match(/^\*\/(\d+)$/)
  if (minute === '0' && everyNHours) {
    const n = parseInt(everyNHours[1], 10)
    if (n === 1) {
      return 'Every hour'
    }
    return `Every ${n} hours`
  }

  // Every N minutes pattern: "*/N * * * *"
  const everyNMinutes = minute.match(/^\*\/(\d+)$/)
  if (everyNMinutes && hour === '*') {
    const n = parseInt(everyNMinutes[1], 10)
    if (n < 60) {
      return `Every ${n} minutes`
    }
    const hours = Math.floor(n / 60)
    return hours === 1 ? 'Every hour' : `Every ${hours} hours`
  }

  // Specific time pattern: "0 14 * * *" = daily at 2 PM
  if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const m = parseInt(minute, 10)
    const h = parseInt(hour, 10)
    const timeStr = formatTime(h, m)
    return `Daily at ${timeStr}`
  }

  // Hour range pattern: "0 9-17/1 * * *" = every hour from 9 AM to 5 PM
  const hourRange = hour.match(/^(\d+)-(\d+)\/(\d+)$/)
  if (minute === '0' && hourRange) {
    const start = parseInt(hourRange[1], 10)
    const end = parseInt(hourRange[2], 10)
    const interval = parseInt(hourRange[3], 10)
    const startTime = formatTime(start, 0)
    const endTime = formatTime(end, 0)
    if (interval === 1) {
      return `Every hour from ${startTime} to ${endTime}`
    }
    return `Every ${interval} hours from ${startTime} to ${endTime}`
  }

  // Every hour at minute 0 pattern: "0 0-23/1 * * *"
  if (minute === '0') {
    const hourPattern = hour.match(/^(\d+)-(\d+)\/(\d+)$/)
    if (hourPattern) {
      const start = parseInt(hourPattern[1], 10)
      const end = parseInt(hourPattern[2], 10)
      if (start === 0 && end === 23) {
        return 'Every hour'
      }
    }
  }

  // Fallback: return a more readable version of the CRON expression
  return `CRON: ${cron}`
}

/**
 * Format a description string (e.g., from NestJS CronExpression)
 */
function formatDescription(desc: string): string {
  // Handle NestJS CronExpression format
  if (desc.includes('EVERY_HOUR')) {
    return 'Every hour'
  }
  if (desc.includes('EVERY_DAY')) {
    return 'Daily'
  }
  if (desc.includes('EVERY_WEEK')) {
    return 'Weekly'
  }
  if (desc.includes('EVERY_MONTH')) {
    return 'Monthly'
  }
  if (desc.includes('EVERY_MINUTE')) {
    return 'Every minute'
  }
  
  // Return as-is if it's already a readable description
  return desc
}

/**
 * Format hour and minute as human-readable time
 */
function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
  const displayMinute = minute === 0 ? '' : `:${minute.toString().padStart(2, '0')}`
  return `${displayHour}${displayMinute} ${period}`
}

